import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, doc, setDoc } from 'firebase/firestore';

const RedirectContext = createContext();

export const useRedirect = () => {
  const context = useContext(RedirectContext);
  if (!context) {
    throw new Error('useRedirect must be used within a RedirectProvider');
  }
  return context;
};

const STORAGE_KEY = 'redirectEnabledByProject';

export const RedirectProvider = ({ children }) => {
  // Per-project toggle: { projectId: boolean }
  const [redirectEnabledMap, setRedirectEnabledMap] = useState({});
  // Per-project redirect links: { projectId: { bookCode: url } }
  const [redirectLinksMap, setRedirectLinksMap] = useState({});

  // Seed from localStorage immediately so the UI shows the last-known state
  // before Firestore responds (avoids a blank/wrong flash on the admin's own browser).
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved !== null) {
        const parsed = JSON.parse(saved);
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
          setRedirectEnabledMap(parsed);
        }
      }
    } catch {
      // ignore corrupt data
    }
  }, []);

  // Subscribe to Firestore projects collection so every device (phone, tablet,
  // other browsers) gets the same redirect-enabled state in real time.
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'projects'),
      (snapshot) => {
        const firestoreMap = {};
        snapshot.forEach((docSnap) => {
          if (docSnap.data().redirectEnabled !== undefined) {
            firestoreMap[docSnap.id] = !!docSnap.data().redirectEnabled;
          }
        });
        if (Object.keys(firestoreMap).length > 0) {
          setRedirectEnabledMap(prev => ({ ...prev, ...firestoreMap }));
        }
      },
      (error) => {
        console.warn('RedirectContext: Firestore snapshot failed, using localStorage only:', error.message);
      }
    );
    return () => unsubscribe();
  }, []);

  // Mirror Firestore state into localStorage so the admin's own browser stays
  // in sync even when offline.
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(redirectEnabledMap));
  }, [redirectEnabledMap]);

  const isRedirectEnabled = useCallback((projectId) => {
    return !!redirectEnabledMap[projectId];
  }, [redirectEnabledMap]);

  // Write to both Firestore (so all devices pick it up) and local state.
  const setRedirectEnabled = useCallback(async (projectId, enabled) => {
    setRedirectEnabledMap(prev => ({ ...prev, [projectId]: enabled }));
    try {
      await setDoc(doc(db, 'projects', projectId), { redirectEnabled: enabled }, { merge: true });
    } catch (error) {
      console.error('RedirectContext: failed to save redirectEnabled to Firestore:', error);
    }
  }, []);

  const getRedirectLinks = useCallback((projectId) => {
    return redirectLinksMap[projectId] || {};
  }, [redirectLinksMap]);

  const setRedirectLinksFromData = useCallback((projectId, books) => {
    const links = {};
    books.forEach(book => {
      if (book.code && book.redirect_link && book.redirect_link.trim() !== '') {
        links[book.code] = book.redirect_link.trim();
      }
    });
    setRedirectLinksMap(prev => ({ ...prev, [projectId]: links }));
  }, []);

  const getRedirectUrl = useCallback((projectId, bookCode) => {
    if (redirectEnabledMap[projectId] && redirectLinksMap[projectId]?.[bookCode]) {
      return redirectLinksMap[projectId][bookCode];
    }
    return null;
  }, [redirectEnabledMap, redirectLinksMap]);

  // Mobile detection utility
  const isMobileDevice = useCallback(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.innerWidth <= 768);
  }, []);

  const value = useMemo(() => ({
    isRedirectEnabled,
    setRedirectEnabled,
    getRedirectLinks,
    setRedirectLinksFromData,
    getRedirectUrl,
    isMobileDevice
  }), [isRedirectEnabled, setRedirectEnabled, getRedirectLinks, setRedirectLinksFromData, getRedirectUrl, isMobileDevice]);

  return (
    <RedirectContext.Provider value={value}>
      {children}
    </RedirectContext.Provider>
  );
};
